import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Component that automatically applies styles from database to translations
 * Usage: Replace <T k="key" /> with <T k="key" />
 */
const T = ({ k, className = '', defaultStyle = {} }) => {
  const { t, i18n } = useTranslation();
  const [styles, setStyles] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    let mounted = true;
    
    const loadStyles = () => {
      try {
        const lang = i18n.language || 'sv';
        const resources = i18n.store?.data?.[lang]?.translation;
        
        if (resources) {
          const styleKey = `_styles_${k}`;
          const savedStyles = resources[styleKey];
          
          if (savedStyles && mounted) {
            setStyles(savedStyles);
          }
        }
      } catch (error) {
        console.error(`[T] Error loading styles for ${k}:`, error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    
    // Load immediately
    loadStyles();
    
    // Load after a short delay (for initial page load)
    const timer = setTimeout(loadStyles, 500);
    
    // Listen for language changes
    const handleLanguageChanged = () => {
      loadStyles();
    };
    
    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      mounted = false;
      clearTimeout(timer);
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [k, i18n.language]);
  
  // Merge default styles with saved styles
  const finalStyle = styles ? {
    ...defaultStyle,
    color: styles.color || defaultStyle.color,
    fontSize: styles.fontSize ? `${styles.fontSize}px` : defaultStyle.fontSize
  } : defaultStyle;
  
  return (
    <span className={className} style={finalStyle}>
      {t(k)}
    </span>
  );
};

// Export both named and default for flexibility
export { T };
export default T;
